import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import {
  Gamepad2,
  Clock,
  Target,
  Zap,
  TrendingUp,
  ChevronRight,
  AlertTriangle,
  Trophy,
  Calendar,
  BarChart3,
  User,
  CheckCircle2,
  XCircle,
  Star,
  Eye,
  EyeOff,
  Settings,
} from 'lucide-react'

interface StudentGameStats {
  student_id: number
  name: string
  total_games: number
  total_time_minutes: number
  avg_accuracy: number
  games_today: number
  xp_this_week: number
}

interface AllStudentsStats {
  students: StudentGameStats[]
  combined: {
    total_games: number
    total_time_minutes: number
    avg_accuracy: number
  }
}

interface GameBreakdown {
  game_id: string
  game_name: string
  times_played: number
  best_accuracy: number | null
  avg_accuracy: number | null
  high_score: number | null
  total_xp: number
  last_played: string | null
}

interface RecentSession {
  id: number
  game_id: string
  game_name: string
  played_at: string | null
  score: number | null
  accuracy: number | null
  questions_correct: number | null
  questions_total: number | null
  duration_seconds: number | null
  xp_earned: number | null
}

interface StudentDetailedStats {
  summary: {
    total_games_played: number
    total_time_minutes: number
    overall_accuracy: number
    total_xp_from_games: number
    favorite_game: string | null
  }
  by_game: GameBreakdown[]
  recent_sessions: RecentSession[]
}

interface SessionQuestion {
  id: number
  question: string
  correct_answer: string
  student_answer: string
  is_correct: boolean
  response_time_ms: number | null
  skill: string | null
}

interface SessionDetail {
  session: {
    id: number
    game_id: string
    game_name: string
    started_at: string | null
    duration_seconds: number | null
    accuracy: number | null
    questions_total: number | null
    questions_correct: number | null
  }
  questions: SessionQuestion[]
  missed_questions: SessionQuestion[]
}

interface StruggleItem {
  item_id: number
  prompt: string
  correct_answer: string
  attempts: number
  accuracy: number
  mastery_level: number
}

interface ContentSetMastery {
  id: number
  name: string
  game: string
  category: string
  total_items: number
  mastered: number
  learning: number
  new: number
  progress_percent: number
  struggling_items: StruggleItem[]
}

interface MasteryReport {
  content_sets: ContentSetMastery[]
}

interface GameSettingsItem {
  id: string
  name: string
  category: string
  is_featured: boolean
  is_hidden: boolean
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '0m'
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
}

function formatDate(isoString: string | null): string {
  if (!isoString) return '-'
  const date = new Date(isoString)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (days === 0) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } else if (days === 1) {
    return 'Yesterday'
  } else if (days < 7) {
    return `${days} days ago`
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

export default function ParentGameStats() {
  const queryClient = useQueryClient()
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null)
  const [showMyStats, setShowMyStats] = useState(false)
  const [expandedSession, setExpandedSession] = useState<number | null>(null)
  const [questionFilter, setQuestionFilter] = useState<'all' | 'missed'>('all')

  // Fetch all students' game stats
  const { data: allStats, isLoading: loadingAll } = useQuery<AllStudentsStats>({
    queryKey: ['parent-game-stats-all'],
    queryFn: async () => {
      const response = await api.get('/api/parent/game-stats/all-students')
      return response.data
    },
  })

  // Fetch detailed stats for selected student
  const { data: studentStats, isLoading: loadingStudent } = useQuery<StudentDetailedStats>({
    queryKey: ['parent-game-stats-student', selectedStudentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${selectedStudentId}/game-stats`)
      return response.data
    },
    enabled: !!selectedStudentId,
  })

  // Fetch mastery report for selected student
  const { data: masteryReport } = useQuery<MasteryReport>({
    queryKey: ['parent-mastery-report', selectedStudentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${selectedStudentId}/mastery-report`)
      return response.data
    },
    enabled: !!selectedStudentId,
  })

  // Fetch session details when expanded
  const { data: sessionDetail } = useQuery<SessionDetail>({
    queryKey: ['parent-game-session', selectedStudentId, expandedSession],
    queryFn: async () => {
      const response = await api.get(
        `/api/parent/students/${selectedStudentId}/game-sessions/${expandedSession}/questions`
      )
      return response.data
    },
    enabled: !!selectedStudentId && !!expandedSession,
  })

  // Fetch featured games for selected student
  const { data: featuredData } = useQuery<{ featured_game_ids: string[] }>({
    queryKey: ['parent-featured-games', selectedStudentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${selectedStudentId}/featured-games`)
      return response.data
    },
    enabled: !!selectedStudentId,
  })

  // Fetch parent's own game stats
  const { data: myStats, isLoading: loadingMyStats } = useQuery<StudentDetailedStats>({
    queryKey: ['parent-my-game-stats'],
    queryFn: async () => {
      const response = await api.get('/api/parent/my-game-stats')
      return response.data
    },
    enabled: showMyStats,
  })

  // Fetch parent's own session details when expanded
  const { data: mySessionDetail } = useQuery<SessionDetail>({
    queryKey: ['parent-my-session', expandedSession],
    queryFn: async () => {
      const response = await api.get(`/api/parent/my-game-sessions/${expandedSession}/questions`)
      return response.data
    },
    enabled: showMyStats && !!expandedSession,
  })

  const featuredGameIds = new Set(featuredData?.featured_game_ids || [])

  // Fetch game settings (all games with hidden/featured flags) for selected student
  const { data: gameSettings } = useQuery<{ games: GameSettingsItem[] }>({
    queryKey: ['parent-game-settings', selectedStudentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${selectedStudentId}/game-settings`)
      return response.data
    },
    enabled: !!selectedStudentId && !showMyStats,
  })

  // Toggle featured game mutation
  const toggleFeatured = useMutation({
    mutationFn: async (gameId: string) => {
      const response = await api.post(
        `/api/parent/students/${selectedStudentId}/featured-games/${gameId}`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-featured-games', selectedStudentId] })
      queryClient.invalidateQueries({ queryKey: ['parent-game-settings', selectedStudentId] })
    },
  })

  // Toggle hidden game mutation
  const toggleHidden = useMutation({
    mutationFn: async (gameId: string) => {
      const response = await api.post(
        `/api/parent/students/${selectedStudentId}/hidden-games/${gameId}`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-game-settings', selectedStudentId] })
    },
  })

  // Auto-select first student if none selected (and not viewing own stats)
  if (!selectedStudentId && !showMyStats && allStats?.students.length) {
    setSelectedStudentId(allStats.students[0].student_id)
  }

  if (loadingAll) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    )
  }

  // Compute active data variables to avoid duplicating JSX
  const activeStats = showMyStats ? myStats : studentStats
  const activeLoading = showMyStats ? loadingMyStats : loadingStudent
  const activeSessionDetail = showMyStats ? mySessionDetail : sessionDetail

  // No early return for empty students — "My Games" tab is always available

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Game Stats</h1>
          <p className="text-gray-400 text-sm">
            {showMyStats ? 'Your personal game stats' : "Track your children's learning game progress"}
          </p>
        </div>
        <Link
          to="/parent/games/arcade"
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition"
        >
          <Gamepad2 className="w-4 h-4" />
          Play Games
        </Link>
      </div>

      {/* Combined Stats (if multiple students, hide when viewing own stats) */}
      {!showMyStats && allStats && allStats.students.length > 1 && (
        <div className="bg-surface-card/50 rounded-xl p-4 border border-gray-700/50">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Combined Family Stats</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400">{allStats.combined.total_games}</div>
              <div className="text-xs text-gray-500">Total Games</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">{Math.round(allStats.combined.total_time_minutes)}m</div>
              <div className="text-xs text-gray-500">Time Played</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">{allStats.combined.avg_accuracy}%</div>
              <div className="text-xs text-gray-500">Avg Accuracy</div>
            </div>
          </div>
        </div>
      )}

      {/* Student Selector */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => {
            setShowMyStats(true)
            setSelectedStudentId(null)
            setExpandedSession(null)
          }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition ${
            showMyStats
              ? 'bg-purple-600 text-white'
              : 'bg-surface-card text-gray-300 hover:bg-surface-hover'
          }`}
        >
          <Gamepad2 className="w-4 h-4" />
          My Games
        </button>
        {allStats?.students.map((student) => (
          <button
            key={student.student_id}
            onClick={() => {
              setShowMyStats(false)
              setSelectedStudentId(student.student_id)
              setExpandedSession(null)
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition ${
              !showMyStats && selectedStudentId === student.student_id
                ? 'bg-purple-600 text-white'
                : 'bg-surface-card text-gray-300 hover:bg-surface-hover'
            }`}
          >
            <User className="w-4 h-4" />
            {student.name}
            {student.games_today > 0 && (
              <span className="bg-green-500/20 text-green-400 text-xs px-1.5 py-0.5 rounded">
                {student.games_today} today
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Stats Display (for selected student or parent's own games) */}
      {(selectedStudentId || showMyStats) && (
        <div className="space-y-6">
          {/* Summary Cards */}
          {activeLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-surface-card rounded-xl p-4 animate-pulse h-24"></div>
              ))}
            </div>
          ) : activeStats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-surface-card rounded-xl p-4">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                  <Gamepad2 className="w-4 h-4" />
                  Games Played
                </div>
                <div className="text-2xl font-bold">{activeStats.summary.total_games_played}</div>
              </div>
              <div className="bg-surface-card rounded-xl p-4">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                  <Clock className="w-4 h-4" />
                  Time Played
                </div>
                <div className="text-2xl font-bold">{Math.round(activeStats.summary.total_time_minutes)}m</div>
              </div>
              <div className="bg-surface-card rounded-xl p-4">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                  <Target className="w-4 h-4" />
                  Accuracy
                </div>
                <div className="text-2xl font-bold text-green-400">{activeStats.summary.overall_accuracy}%</div>
              </div>
              <div className="bg-surface-card rounded-xl p-4">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                  <Zap className="w-4 h-4" />
                  XP Earned
                </div>
                <div className="text-2xl font-bold text-yellow-400">{activeStats.summary.total_xp_from_games}</div>
              </div>
            </div>
          ) : null}

          {/* Per-Game Breakdown */}
          {activeStats && activeStats.by_game.length > 0 && (
            <div className="bg-surface-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-700/50">
                <h3 className="font-semibold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                  Per-Game Stats
                </h3>
              </div>
              <div className="divide-y divide-gray-700/50">
                {activeStats.by_game.map((game) => (
                  <div key={game.game_id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {!showMyStats && (
                        <button
                          onClick={() => toggleFeatured.mutate(game.game_id)}
                          className="p-1 rounded hover:bg-surface-hover transition"
                          title={featuredGameIds.has(game.game_id) ? 'Remove from Featured' : 'Add to Featured'}
                        >
                          <Star
                            className={`w-4 h-4 ${
                              featuredGameIds.has(game.game_id)
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-500'
                            }`}
                          />
                        </button>
                      )}
                      <div>
                        <div className="font-medium">{game.game_name}</div>
                        <div className="text-sm text-gray-400">
                          {game.times_played} plays &middot; Last: {formatDate(game.last_played)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <div className="font-medium text-green-400">
                          {game.best_accuracy ? `${game.best_accuracy}%` : '-'}
                        </div>
                        <div className="text-xs text-gray-500">Best</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-blue-400">
                          {game.avg_accuracy ? `${game.avg_accuracy}%` : '-'}
                        </div>
                        <div className="text-xs text-gray-500">Avg</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-yellow-400">{game.total_xp}</div>
                        <div className="text-xs text-gray-500">XP</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Game Management (only for students, shows all active games) */}
          {!showMyStats && selectedStudentId && gameSettings && gameSettings.games.length > 0 && (
            <div className="bg-surface-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-700/50">
                <h3 className="font-semibold flex items-center gap-2">
                  <Settings className="w-5 h-5 text-gray-400" />
                  Game Management
                </h3>
                <p className="text-xs text-gray-500 mt-1">Control which games are visible and featured for this student</p>
              </div>
              <div className="divide-y divide-gray-700/50">
                {gameSettings.games.map((game) => (
                  <div
                    key={game.id}
                    className={`p-4 flex items-center justify-between ${
                      game.is_hidden ? 'opacity-50' : ''
                    }`}
                  >
                    <div>
                      <div className="font-medium">{game.name}</div>
                      <div className="text-xs text-gray-500">{game.category}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleFeatured.mutate(game.id)}
                        className="p-1.5 rounded hover:bg-surface-hover transition"
                        title={game.is_featured ? 'Remove from Featured' : 'Add to Featured'}
                      >
                        <Star
                          className={`w-4 h-4 ${
                            game.is_featured
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-500'
                          }`}
                        />
                      </button>
                      <button
                        onClick={() => toggleHidden.mutate(game.id)}
                        className="p-1.5 rounded hover:bg-surface-hover transition"
                        title={game.is_hidden ? 'Show game' : 'Hide game'}
                      >
                        {game.is_hidden ? (
                          <EyeOff className="w-4 h-4 text-red-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-green-400" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mastery / Struggling Items (only for students) */}
          {!showMyStats && masteryReport && masteryReport.content_sets.length > 0 && (
            <div className="bg-surface-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-700/50">
                <h3 className="font-semibold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  Content Mastery
                </h3>
              </div>
              <div className="divide-y divide-gray-700/50">
                {masteryReport.content_sets.map((set) => (
                  <div key={set.id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">{set.name}</div>
                      <div className="text-sm text-gray-400">{set.progress_percent}% mastered</div>
                    </div>
                    {/* Progress bar */}
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden mb-3">
                      <div
                        className="h-full bg-gradient-to-r from-purple-500 to-green-500"
                        style={{ width: `${set.progress_percent}%` }}
                      ></div>
                    </div>
                    <div className="flex gap-4 text-xs text-gray-400 mb-3">
                      <span className="text-green-400">{set.mastered} mastered</span>
                      <span className="text-yellow-400">{set.learning} learning</span>
                      <span className="text-gray-500">{set.new} new</span>
                    </div>
                    {/* Struggling items */}
                    {set.struggling_items.length > 0 && (
                      <div className="bg-surface-base/50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-amber-400 text-sm mb-2">
                          <AlertTriangle className="w-4 h-4" />
                          Needs Practice
                        </div>
                        <div className="space-y-2">
                          {set.struggling_items.slice(0, 3).map((item) => (
                            <div key={item.item_id} className="text-sm flex justify-between">
                              <span className="text-gray-300">{item.prompt}</span>
                              <span className="text-red-400">{item.accuracy}% ({item.attempts} tries)</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Sessions */}
          {activeStats && activeStats.recent_sessions.length > 0 && (
            <div className="bg-surface-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-700/50">
                <h3 className="font-semibold flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  Recent Game Sessions
                </h3>
              </div>
              <div className="divide-y divide-gray-700/50">
                {activeStats.recent_sessions.map((session) => (
                  <div key={session.id}>
                    <button
                      onClick={() => {
                        setExpandedSession(expandedSession === session.id ? null : session.id)
                        setQuestionFilter('all')
                      }}
                      className="w-full p-4 flex items-center justify-between hover:bg-gray-750 transition text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
                          <Gamepad2 className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <div className="font-medium">{session.game_name}</div>
                          <div className="text-sm text-gray-400">
                            {formatDate(session.played_at)} &middot; {formatDuration(session.duration_seconds)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="font-medium">
                            {session.questions_correct}/{session.questions_total}
                            <span className={`ml-2 ${
                              (session.accuracy || 0) >= 80 ? 'text-green-400' :
                              (session.accuracy || 0) >= 60 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              ({session.accuracy}%)
                            </span>
                          </div>
                          <div className="text-sm text-yellow-400">+{session.xp_earned} XP</div>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-gray-400 transition ${
                          expandedSession === session.id ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </button>

                    {/* Expanded Session Detail */}
                    {expandedSession === session.id && activeSessionDetail && (
                      <div className="bg-surface-base/50 border-t border-gray-700/50 p-4">
                        {/* Perfect banner */}
                        {activeSessionDetail.missed_questions.length === 0 && (
                          <div className="flex items-center gap-2 text-green-400 mb-4 bg-green-400/10 rounded-lg p-3">
                            <Trophy className="w-5 h-5" />
                            Perfect score! All questions correct.
                          </div>
                        )}

                        {/* Summary + filter toggle */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm">
                            <span className={`font-medium ${
                              (activeSessionDetail.session.accuracy || 0) >= 80 ? 'text-green-400' :
                              (activeSessionDetail.session.accuracy || 0) >= 60 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {activeSessionDetail.session.questions_correct}/{activeSessionDetail.session.questions_total} correct
                            </span>
                          </div>
                          <div className="flex bg-surface-card rounded-lg p-0.5 text-xs">
                            <button
                              onClick={() => setQuestionFilter('all')}
                              className={`px-3 py-1 rounded-md transition ${
                                questionFilter === 'all'
                                  ? 'bg-purple-600 text-white'
                                  : 'text-gray-400 hover:text-gray-300'
                              }`}
                            >
                              All
                            </button>
                            <button
                              onClick={() => setQuestionFilter('missed')}
                              className={`px-3 py-1 rounded-md transition ${
                                questionFilter === 'missed'
                                  ? 'bg-purple-600 text-white'
                                  : 'text-gray-400 hover:text-gray-300'
                              }`}
                            >
                              Missed Only
                            </button>
                          </div>
                        </div>

                        {/* Question list */}
                        {(() => {
                          const questions = questionFilter === 'all'
                            ? activeSessionDetail.questions
                            : activeSessionDetail.missed_questions
                          if (questions.length === 0) {
                            return (
                              <div className="text-sm text-gray-500 text-center py-4">
                                No missed questions in this session.
                              </div>
                            )
                          }
                          return (
                            <div className="space-y-2">
                              {questions.map((q) => (
                                <div key={q.id} className={`bg-surface-card rounded-lg p-3 border-l-2 ${
                                  q.is_correct ? 'border-green-500' : 'border-red-500'
                                }`}>
                                  <div className="flex items-start gap-2 mb-2">
                                    {q.is_correct ? (
                                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                    ) : (
                                      <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                                    )}
                                    <span className="text-sm text-gray-300">{q.question}</span>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ml-6">
                                    <div>
                                      <span className="text-gray-500">Answer: </span>
                                      <span className={q.is_correct ? 'text-green-400' : 'text-red-400'}>
                                        {q.student_answer || '(no answer)'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Correct: </span>
                                      <span className="text-green-400">{q.correct_answer}</span>
                                    </div>
                                    {q.response_time_ms != null && (
                                      <span className="text-gray-500">
                                        {(q.response_time_ms / 1000).toFixed(1)}s
                                      </span>
                                    )}
                                    {q.skill && (
                                      <span className="bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">
                                        {q.skill}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        })()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
